import { mapByKey } from '~/utils/commonUtils';
import { post } from '~/utils/api';
import { defineStore } from 'pinia';

import useFetch from '~/composables/api/useFetch';
import { useBrand } from '~/composables/useBrand';
import { getImageUrl, normalizePromotions, selectUpc } from '~/modules/gwp/utils';

import type {
  GwpState,
  Promotions,
  GwpBadge,
  Promotion,
  GiftProduct,
  GetPromotionByIdGetter,
  GetGiftProductByPromoIdGetter,
  GetSelectedUpcIdGetter,
  GetSelectedUpcGetter,
  IsThresholdMetGetter,
  GetOnlyMetPromotionsGetter,
  GetPreparedGwpPayloadGetter,
  GwpPayloadItem,
  GetPropsByPromoIdGetter,
} from '~/modules/gwp/types';

const { isMcom } = useBrand();

export default defineStore('gwp', {
  state: (): GwpState => ({
    promotionsByProductId: {},
    selectedProductId: null,
    isGiftInBagStatusLoading: true,
  }),
  getters: {
    getPromotions(): Promotion[] {
      const currentPromotions = this.selectedProductId ? this.promotionsByProductId[this.selectedProductId] : undefined;

      if (!currentPromotions) {
        return [];
      }

      const { byId, order } = currentPromotions;

      return order.map((id) => byId[id]).filter((p): p is Promotion => !!p);
    },

    getPromotionById(): GetPromotionByIdGetter {
      return (promoId) => {
        return this.selectedProductId ? this.promotionsByProductId[this.selectedProductId]?.byId[promoId] : undefined;
      };
    },

    getGiftProductByPromoId(): GetGiftProductByPromoIdGetter {
      return (promoId) => {
        const promotion = this.getPromotionById(promoId);
        const giftProduct = Object.values(promotion?.giftProductsById || {})[0];
        return giftProduct;
      };
    },

    getSelectedUpcId(): GetSelectedUpcIdGetter {
      return (promoId) => {
        const giftProduct = this.getGiftProductByPromoId(promoId);
        return giftProduct?.giftSelectedUpcId || 0;
      };
    },

    getSelectedUpc(): GetSelectedUpcGetter {
      return (promoId) => {
        const product = this.getGiftProductByPromoId(promoId);
        if (!product) return undefined;
        const { giftFormattedUpcs, giftSelectedUpcId } = product;
        return giftFormattedUpcs?.[giftSelectedUpcId];
      };
    },

    isThresholdMet(): IsThresholdMetGetter {
      return (promoId, lowestSalePriceTier) => {
        const promo = this.getPromotionById(promoId);

        if (!promo || !lowestSalePriceTier) return false;

        const value = lowestSalePriceTier?.values?.[0]?.value ?? 0;
        return promo.threshold <= value;
      };
    },

    getOnlyMetPromotions(): GetOnlyMetPromotionsGetter {
      return (lowestSalePriceTier) => {
        const promotionsByIdObj = this.selectedProductId ? this.promotionsByProductId?.[this.selectedProductId]?.byId : undefined;

        if (!promotionsByIdObj) return [];

        return Object.values(promotionsByIdObj).filter((promotion): promotion is Promotion => {
          const isThresholdMet = this.isThresholdMet(promotion?.promoId, lowestSalePriceTier);
          return !!isThresholdMet;
        });
      };
    },

    getPreparedGwpPayload(): GetPreparedGwpPayloadGetter {
      return (lowestSalePriceTier) => {
        const metPromotions = this.getOnlyMetPromotions(lowestSalePriceTier);

        return metPromotions
          .map((promotion) => {
            const giftProduct = this.getGiftProductByPromoId(promotion.promoId);
            const giftSelectedUpc = this.getSelectedUpc(promotion.promoId);
            const giftSelectedUpcId = this.getSelectedUpcId(promotion.promoId);

            if (!giftSelectedUpc || !giftSelectedUpcId) {
              console.warn(`No selected UPC found for promotion ID: ${promotion.promoId}`);
              return null;
            }

            const isMakeAChoice = giftProduct?.id ? promotion?.giftProductsById?.[giftProduct?.id]?.isMultipleGiftUpcs : false;

            return {
              promotionId: promotion.promoId,
              upcId: giftSelectedUpcId,
              upcNumber: giftSelectedUpc?.upcNumber,
              isMakeAChoice,
              quantity: 1,
            };
          })
          .filter((item): item is GwpPayloadItem => !!item);
      };
    },
    getPropsByPromoId(): GetPropsByPromoIdGetter {
      return (promoId, lowestSalePriceTier) => {
        const promotion = this.getPromotionById(promoId);
        const giftProduct = this.getGiftProductByPromoId(promoId);
        const selectedUpc = this.getSelectedUpc(promoId);
        const isThresholdMet = this.isThresholdMet(promoId, lowestSalePriceTier);
        const imageUrl = getImageUrl(giftProduct?.urlTemplate?.product, selectedUpc?.images?.[0]?.filePath);
        const showCtaButtons = !!(giftProduct?.isMultipleGiftUpcs && !giftProduct?.alreadyInBag);

        return {
          title: promotion?.title || '',
          description: (isMcom ? promotion?.description : promotion?.header) || '',
          image: imageUrl,
          isThresholdMet,
          isSwapButtonAvailable: isThresholdMet && showCtaButtons,
          alreadyInBag: giftProduct?.alreadyInBag || false,
          spendMoreToUnlock: !isThresholdMet,
        };
      };
    },
  },
  actions: {
    setSelectedGift({ promotions }: { promotions: GwpBadge[] }): void {
      const productId = this.selectedProductId;

      if (!productId) {
        console.warn('Cannot set gift: selectedProductId is missing');
        return;
      }

      if (this.promotionsByProductId[productId]) return;

      const normalizedPromotions = normalizePromotions(promotions);

      if (!this.promotionsByProductId[productId]) {
        this.promotionsByProductId[productId] = {
          byId: {},
          order: [],
          upcsInBag: [],
        };
      }

      this.promotionsByProductId[productId].byId = mapByKey(normalizedPromotions, 'promoId');
      this.promotionsByProductId[productId].order = normalizedPromotions.map((promo: Promotion) => promo.promoId);
    },

    setUpcsInBag({ upcsInBag }: { upcsInBag: Promotions['upcsInBag'] }): void {
      const productPromotions = this.selectedProductId ? this.promotionsByProductId[this.selectedProductId] : undefined;

      if (!productPromotions) return;
      productPromotions.upcsInBag = [...upcsInBag];
    },

    setSelectedUpcId({
      promoId,
      giftProductId,
      giftSelectedUpcId,
      alreadyInBag,
    }: {
      promoId: Promotion['promoId'];
      giftProductId: GiftProduct['id'];
      giftSelectedUpcId: GiftProduct['giftSelectedUpcId'];
      alreadyInBag: GiftProduct['alreadyInBag'];
    }) {
      const promotion = this.getPromotionById(promoId);
      const giftProduct = promotion?.giftProductsById[giftProductId];

      if (giftProduct) {
        giftProduct.giftSelectedUpcId = giftSelectedUpcId;
        giftProduct.alreadyInBag = alreadyInBag;
      } else {
        console.warn(`No promotion found for Promo ID: ${promoId}`);
      }
    },

    setSelectedProductId(productId: GiftProduct['id']) {
      this.selectedProductId = productId;
    },

    initializeGwp({ promotions }: { promotions: GwpBadge[] }): void {
      if (!promotions || promotions.length === 0) return;

      if (this.selectedProductId && !this.promotionsByProductId[this.selectedProductId]) {
        this.setSelectedGift({ promotions });
      }
    },

    async fetchGiftInBagStatus({ disableLoadingIndicator = false }: { disableLoadingIndicator?: boolean } = {}): Promise<void> {
      this.isGiftInBagStatusLoading = !disableLoadingIndicator;

      const { result, fetch } = useFetch<{ bagGwpUpcs: Promotions['upcsInBag'] }>(() =>
        post('/xapi/digital/v1/product/gwp/promoBasedOnBag', {
          qualifiedPromotionsRequest: {
            products: [{ productId: this.selectedProductId, quantity: 1 }],
          },
        }),
      );

      try {
        await fetch();
        const bagGwpUpcs = result?.value?.bagGwpUpcs || [];
        this.setUpcsInBag({ upcsInBag: bagGwpUpcs });
      } catch (error) {
        this.setUpcsInBag({ upcsInBag: [] });
        console.error('Error fetching GWP gift in bag status:', error);
      } finally {
        this.isGiftInBagStatusLoading = false;
      }
    },

    preselectUpcForEveryPromotion(): void {
      const promotions = this.getPromotions;
      if (promotions.length === 0) return;

      const { upcsInBag } = (this.selectedProductId && this.promotionsByProductId?.[this.selectedProductId]) || { upcsInBag: [] };

      promotions.forEach((promo) => {
        Object.values(promo.giftProductsById).forEach((giftProduct) => {
          const upcs = Object.values(giftProduct.giftFormattedUpcs || {});
          const selection = selectUpc(upcs, upcsInBag, giftProduct.giftSelectedUpcId);

          if (selection) {
            this.setSelectedUpcId({
              promoId: promo.promoId,
              giftProductId: giftProduct.id,
              giftSelectedUpcId: selection.upcId,
              alreadyInBag: selection.alreadyInBag,
            });
          }
        });
      });
    },
  },
});
